import { useState } from "react";
import { Download, FileText, X } from "lucide-react";
import { Button } from "../button";
import { Input } from "../input";

export interface MedicationUploadProps {
  onSubmit?: (files: File[]) => void;
  initialFiles?: File[];
}

interface UploadedFile {
  file: File;
  id: string;
}

export function MedicationUpload({ onSubmit, initialFiles = [] }: MedicationUploadProps) {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>(
    initialFiles.map((file) => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
    }))
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter(
      (file) => file.type === "text/csv" || file.name.endsWith(".csv")
    );

    const newFiles: UploadedFile[] = files.map((file) => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
    }));

    setUploadedFiles((prev) => [...prev, ...newFiles]);
  };

  const removeFile = (id: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleSubmit = () => {
    const files = uploadedFiles.map((f) => f.file);
    onSubmit?.(files);
  };

  const downloadExample = () => {
    // Create example CSV content
    const csvContent = `medication_name,dosage,supply_duration,date_prescribed,provider_name
Lisinopril,0.1 mg,3 Month,12/15/2024,Dr. Smith
Metformin,500 mg,1 Month,12/10/2024,Dr. Johnson
Atorvastatin,20 mg,3 Month,12/08/2024,Dr. Williams`;

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "medication_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="flex-1 overflow-y-auto space-y-6 pr-2">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Upload Medication CSV Files</h3>
          <Button variant="outline" size="sm" onClick={downloadExample} className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            Download Example
          </Button>
        </div>
      </div>

      {/* File Upload */}
      <div className="space-y-2">
        <Input
          id="medication-upload"
          type="file"
          multiple
          accept=".csv"
          onChange={handleFileSelect}
          placeholder="Choose CSV files"
        />
      </div>

      {/* Uploaded Files List */}
      {uploadedFiles.length > 0 && (
        <div className="space-y-4">
          <h4 className="font-medium">Uploaded Files</h4>
          <div className="space-y-2">
            {uploadedFiles.map((uploadedFile) => (
              <div key={uploadedFile.id} className="flex items-center gap-3 p-3 border rounded-lg">
                <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{uploadedFile.file.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(uploadedFile.file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => removeFile(uploadedFile.id)} className="h-8 w-8 p-0">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Submit Button */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button variant="outline" onClick={() => setUploadedFiles([])} disabled={uploadedFiles.length === 0}>
          Clear All
        </Button>
        <Button onClick={handleSubmit} disabled={uploadedFiles.length === 0}>
          Import Medications
        </Button>
      </div>
    </div>
  );
}
